import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Space, Typography, Button, message, Select, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import StandardTable, { createActionColumn } from '../StandardTable';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { useGetCorFormGroup } from '../../../hooks/genericHooks';

// Resolve module-sensitive form definition table from current route
function getFormDefTableForPath(pathname: string): { table: string; pageTitle: string } {
  if (pathname.startsWith('/qaqc')) return { table: 'qaqc_form_definitions', pageTitle: 'QAQC Form Definitions' };
  if (pathname.startsWith('/it')) return { table: 'it_form_definitions', pageTitle: 'IT Form Definitions' };
  if (pathname.startsWith('/to')) return { table: 'to_form_definitions', pageTitle: 'TO Form Definitions' };
  if (pathname.startsWith('/hse')) return { table: 'hse_form_definitions', pageTitle: 'HSE Form Definitions' };
  if (pathname.startsWith('/admin')) return { table: 'adm_form_definitions', pageTitle: 'Admin Form Definitions' };
  if (pathname.startsWith('/construction')) return { table: 'const_form_definitions', pageTitle: 'Construction Form Definitions' };
  if (pathname.startsWith('/material')) return { table: 'mat_form_definitions', pageTitle: 'Material Control Form Definitions' };
  if (pathname.startsWith('/machinery')) return { table: 'mach_form_definitions', pageTitle: 'Machinery Form Definitions' };
  if (pathname.startsWith('/dcc')) return { table: 'dcc_form_definitions', pageTitle: 'DCC Form Definitions' };
  if (pathname.startsWith('/official')) return { table: 'off_form_definitions', pageTitle: 'Official Affairs Form Definitions' };
  if (pathname.startsWith('/design')) return { table: 'dsgn_form_definitions', pageTitle: 'Design & Engineering Form Definitions' };
  return { table: '', pageTitle: 'Form Definitions' };
}

const FormDefinitionsList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { table, pageTitle } = useMemo(() => getFormDefTableForPath(location.pathname), [location.pathname]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const { data: formGroups } = useGetCorFormGroup();
  const groupMap = useMemo(() => {
    const m = new Map<number, any>();
    const arr = ((formGroups as any)?.data as any[]) || [];
    const moduleRoute = '/' + (location.pathname.split('/')[1] || '');
    const filtered = arr.filter((g: any) => !g?.route_name || g.route_name === moduleRoute);
    filtered.forEach((g: any) => {
      // assuming fields: id, group_name (fallbacks guarded)
      m.set(Number(g.id), g);
    });
    return m;
  }, [formGroups, location.pathname]);

  const groupOptions = useMemo(() => {
    return Array.from(groupMap.values()).map((g: any) => ({ value: Number(g.id), label: g.group_name || String(g.id) }));
  }, [groupMap]);

  const loadData = async () => {
    if (!table) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      // Fetch primary fields for quick visualization. Adjust columns below if schema extends.
      const query = `SELECT id, group_id, form_description, form_ref, reference_document, numbering_prefix, created_by FROM ${table} ORDER BY id DESC`;
      const data = await fetchSqlDataWithQuery(query);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('Failed to fetch form definitions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /*eslint-disable-line*/ }, [table]);

  const filteredRows = useMemo(() => {
    const data = Array.isArray(rows) ? rows : [];
    const byGroup = selectedGroupId ? data.filter(r => Number(r.group_id) === Number(selectedGroupId)) : data;
    const terms = (searchText || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return byGroup;
    return byGroup.filter(r => {
      const hay = [
        r.form_description,
        r.form_ref,
        r.reference_document,
        r.numbering_prefix,
        r.created_by,
      ]
        .map(v => (v == null ? '' : String(v)))
        .join(' ')
        .toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  }, [rows, selectedGroupId, searchText]);

  const columns: any[] = [
    {
      title: 'Form Group',
      dataIndex: 'group_id',
      width: 180,
      render: (_: any, rec: any) => {
        const g = groupMap.get(Number(rec.group_id));
        return g?.group_name || rec.group_id || '';
      },
      exportValue: (rec: any) => {
        const g = groupMap.get(Number(rec.group_id));
        return g?.group_name || rec.group_id || '';
      }
    },
    { title: 'Form Description', dataIndex: 'form_description', width: 260 },
    { title: 'Form Ref', dataIndex: 'form_ref', width: 160 },
    { title: 'Reference Document', dataIndex: 'reference_document', width: 220 },
    { title: 'Numbering Prefix', dataIndex: 'numbering_prefix', width: 180 },
    { title: 'Created By', dataIndex: 'created_by', width: 160 },
    createActionColumn({
      title: 'Actions',
      onView: (rec: any) => {
        // Navigate to a read-only view of a generated form definition if such a page exists
        // Fallback: open generator in a neutral route or display info
        message.info(`Form Definition #${rec.id}`);
      },
      onEdit: (rec: any) => {
        // Navigate to the module-aware definition editor route
        const match = location.pathname.match(/^\/(\w+)/);
        const module = match ? match[1] : '';
        if (!module) {
          message.warning('Unknown module route.');
          return;
        }
        navigate(`/${module}/forms/definitions/edit/${rec.id}`);
      },
      showDelete: false,
    }),
  ];

  // Optional: derive a base module to place a "Create" button in the header
  const baseModule = useMemo(() => {
    const match = location.pathname.match(/^\/(\w+)/);
    return match ? match[1] : '';
  }, [location.pathname]);

  return (
    <Card style={{ margin: '0 auto', padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>{pageTitle}</Typography.Title>
          <div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                if (!baseModule) {
                  message.warning('Unknown module route.');
                  return;
                }
                // Keep users inside the module UX: use module-local generator route for all modules except core.
                if (baseModule !== 'core') {
                  navigate(`/${baseModule}/forms/definitions/create`);
                  return;
                }
                // Core module: generator already lives here.
                navigate('/core/general/pdf-to-react');
              }}
            >
              Create New Form
            </Button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Select
            allowClear
            showSearch
            placeholder="Filter by Form Group"
            style={{ minWidth: 240 }}
            value={selectedGroupId as any}
            onChange={(val) => setSelectedGroupId(val ?? null)}
            options={groupOptions}
            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
          />
          <Input
            allowClear
            placeholder="Filter (AND across other columns)"
            style={{ minWidth: 280, flex: 1 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <StandardTable
          columns={columns}
          dataSource={filteredRows}
          rowKey={(r) => String(r.id)}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Space>
    </Card>
  );
};

export default FormDefinitionsList;
